import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { GET_PLAY_BY_PLAY_EVENTS } from '../GamePlayByPlay/GetPlayByPlayEvents';
import { LoadingDots } from './GameOverview.styles';
import { composePlayDescription, resolvePenaltyText } from './PlayDescription';

// Description cell for a play row: loads the play's events, then shows the
// action note followed by any penalty text. Used by the Play by Play drive
// sub-table and by Scoring Plays so both load and fail the same way.
//
// Pass `api` and `autoSizeColumnId` where the column should hug its text.
// Descriptions load async, so the grid's first auto-size pass measures empty
// cells; this re-runs it once the events arrive. Leave both out for a
// flex column, which auto-size would freeze at its current width.
function PlayDescriptionCell({ play, api, autoSizeColumnId }) {
  const { data, loading, error } = useQuery(GET_PLAY_BY_PLAY_EVENTS, {
    variables: { playId: play?.id },
    skip: !play?.id,
  });

  useEffect(() => {
    if (data?.playEvents && api && autoSizeColumnId) {
      api.autoSizeColumns([autoSizeColumnId]);
    }
  }, [data, api, autoSizeColumnId]);

  if (!play) return null;
  if (loading && !data) {
    return (
      <LoadingDots aria-label="Loading description">
        <span />
        <span />
        <span />
      </LoadingDots>
    );
  }
  // A failed request must not look like a play with no description.
  if (error && !data) return 'Description unavailable';
  const combined = [
    composePlayDescription(play, data?.playEvents),
    resolvePenaltyText(play),
  ]
    .filter(Boolean)
    .join('; ');
  return combined || '-';
}

PlayDescriptionCell.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  play: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  api: PropTypes.object,
  autoSizeColumnId: PropTypes.string,
};

PlayDescriptionCell.defaultProps = {
  play: null,
  api: null,
  autoSizeColumnId: undefined,
};

export default PlayDescriptionCell;
